import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

type MobileModalPortalProps = {
  children: React.ReactNode;
  /** Click backdrop to close */
  onClose?: () => void;
  /**
   * sheet — bottom sheet on mobile, centered card on desktop (default)
   * page — full-viewport sticky page on mobile (no background scroll jump)
   */
  layout?: 'sheet' | 'page';
  /** @deprecated use layout="sheet" / layout="page" */
  sheetOnMobile?: boolean;
};

/**
 * Renders modals on document.body above the app bottom nav (z-50) and sidebar (z-60).
 * Locks body scroll so the page behind does not jump when the dialog opens.
 */
export function MobileModalPortal({
  children,
  onClose,
  layout,
  sheetOnMobile = true,
}: MobileModalPortalProps) {
  const mode = layout ?? (sheetOnMobile ? 'sheet' : 'page');

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    // Freeze scroll position on iOS (overflow:hidden alone often still jumps).
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouch;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const shellClass =
    mode === 'page'
      ? 'fixed inset-0 z-[120] flex items-stretch justify-center overflow-hidden bg-zinc-950/50'
      : 'fixed inset-0 z-[120] flex items-end justify-center overflow-hidden bg-black/45 p-0 sm:items-center sm:p-4';

  const panelClass =
    mode === 'page'
      ? 'flex h-full w-full min-w-0 max-w-full flex-col overflow-hidden bg-transparent sm:h-auto sm:max-h-[min(92dvh,900px)] sm:max-w-lg sm:p-4'
      : 'w-full min-w-0 sm:w-auto';

  const modal = (
    <div
      className={shellClass}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className={panelClass} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : modal;
}
